/**
 * Settings inherited from FOCTTA Core (release 3.1), on the canonical Core
 * integration page (/admin/integrations/foctta-core; no parallel Core surface,
 * IMPLEMENTATION_TRACKER entry #94).
 *
 * Each setting shows where it comes from: "Inherited from FOCTTA Core" (with
 * what Core says it is) or "Configured in Discovery", and the IT Administrator
 * switches between them (foundational:integration:update). The DPO and the
 * Auditor read the same card with nothing to change. People and roles always
 * show "Configured in Discovery": they are never inherited.
 */
import { useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, Link2, Loader2, RefreshCw } from 'lucide-react';

import { GlassCard, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useCan } from '@/lib/access';
import {
  getInheritedSettings,
  setInheritedSource,
  type InheritedDomainView,
  type InheritedSettingsView,
  type SuiteDomain,
} from '@/lib/api';

const LABELS: Record<SuiteDomain, { title: string; inherit: string; local: string }> = {
  IDENTITY: {
    title: 'Single sign-on',
    inherit:
      'The sign-in page offers "Sign in with SSO" through FOCTTA Core: people sign in with what they use there (ADFS, SAML, OIDC, LDAP or Core passwords with MFA).',
    local: "Discovery's own SSO (Settings > SSO / IdP), LDAP and passwords.",
  },
  MAIL: {
    title: 'Email out',
    inherit: "Invitations, password links and notifications go out through FOCTTA Core's mail carrier.",
    local: "Discovery's own mail sender (Notify providers and the platform sender).",
  },
  LIFECYCLE: {
    title: 'Leavers',
    inherit:
      'A person switched off in FOCTTA Core (Settings, SCIM, directory) is suspended here and signed out of every Discovery session.',
    local: "FOCTTA Core's leaver signals are recorded and ignored; Discovery's administrators and SCIM handle leavers.",
  },
  PEOPLE: {
    title: 'People and roles',
    inherit: '',
    local:
      "Always Discovery's own: the IT Administrator gives each person a Discovery account and role (Settings > Users). Never taken from FOCTTA Core.",
  },
};

function describeValue(d: InheritedDomainView): string | null {
  const v = d.inherited as Record<string, unknown> | null;
  if (!v) return null;
  if (d.domain === 'IDENTITY' && typeof v['issuer'] === 'string') return `${v['issuer']} (client ${String(v['clientId'] ?? '')})`;
  if (d.domain === 'MAIL') {
    const carrier = typeof v['carrier'] === 'string' ? v['carrier'] : 'none';
    return `${carrier}${typeof v['sender'] === 'string' && v['sender'] ? `, from ${v['sender']}` : ''}`;
  }
  if (d.domain === 'LIFECYCLE' && typeof v['lifecycle'] === 'string') {
    return v['lifecycle'] === 'signals' ? 'Signals from FOCTTA Core' : 'None';
  }
  return null;
}

export function InheritedFromCoreCard(): JSX.Element {
  const canChange = useCan('foundational:integration:update');
  const [view, setView] = useState<InheritedSettingsView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<SuiteDomain | 'refresh' | null>(null);

  async function load(fresh = false): Promise<void> {
    try {
      setView(await getInheritedSettings(fresh));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not read the inherited settings.');
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function choose(domain: SuiteDomain, source: 'INHERIT' | 'LOCAL'): Promise<void> {
    setBusy(domain);
    try {
      setView(await setInheritedSource(domain, source));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The setting was not changed.');
    } finally {
      setBusy(null);
    }
  }

  return (
    <GlassCard data-testid="inherited-from-core">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Link2 className="h-4 w-4" /> Settings from FOCTTA Core
        </CardTitle>
        <CardDescription clamp>
          When FOCTTA Core pairs this Discovery (its IT Administrator presses &quot;Pair with
          Discovery&quot; in Core), single sign-on, mail and leavers configured in Core are used here
          without being set up again. Each can be configured in Discovery instead.
        </CardDescription>
      </CardHeader>
      <div className="space-y-4 px-6 pb-6">
        {error ? (
          <div className="flex gap-2 rounded-md border border-red-500/40 bg-red-500/5 p-3 text-sm text-red-700 dark:text-red-300">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        ) : null}
        {!view && !error ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Reading the settings...
          </div>
        ) : null}
        {view ? (
          <>
            <div className="flex flex-wrap items-center gap-3 text-sm" data-testid="inherited-pairing">
              {view.paired && view.pairing ? (
                <>
                  <Badge variant="outline" className="border-emerald-500/40 text-emerald-700 dark:text-emerald-300">
                    <CheckCircle2 className="mr-1 h-3 w-3" /> Paired with {view.pairing.consentDisplayName ?? 'FOCTTA Core'}
                  </Badge>
                  <span className="text-muted-foreground">
                    {view.pairing.consentApiUrl}, since {new Date(view.pairing.pairedAt).toLocaleString()}
                  </span>
                </>
              ) : (
                <span className="text-muted-foreground">
                  Not paired with FOCTTA Core: every setting is configured in Discovery.
                </span>
              )}
              {view.paired ? (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={busy !== null}
                  onClick={() => {
                    setBusy('refresh');
                    void load(true).finally(() => setBusy(null));
                  }}
                >
                  {busy === 'refresh' ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <RefreshCw className="mr-1 h-3 w-3" />}
                  Read again
                </Button>
              ) : null}
            </div>
            {view.linkError ? (
              <div className="flex gap-2 rounded-md border border-amber-500/40 bg-amber-500/5 p-3 text-sm text-amber-800 dark:text-amber-300">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{view.linkError} Until it answers, Discovery&apos;s own settings apply; passwords always work.</span>
              </div>
            ) : null}
            <ul className="divide-y divide-border rounded-md border">
              {view.domains.map((d) => {
                const label = LABELS[d.domain];
                const inherited = d.source === 'INHERIT';
                const value = describeValue(d);
                return (
                  <li key={d.domain} className="flex flex-col gap-2 p-4 sm:flex-row sm:items-start sm:justify-between" data-testid={`inherited-${d.domain}`}>
                    <div className="space-y-1">
                      <div className="font-medium">{label.title}</div>
                      <div className="text-sm" data-testid={`inherited-${d.domain}-source`}>
                        {inherited ? 'Inherited from FOCTTA Core' : 'Configured in Discovery'}
                        {d.decided ? '' : ' (default)'}
                      </div>
                      <div className="text-xs text-muted-foreground">{inherited ? label.inherit : label.local}</div>
                      {inherited && value ? (
                        <div className="text-xs text-muted-foreground">Core says: {value}</div>
                      ) : null}
                      {d.problem ? (
                        <div className="text-xs text-amber-700 dark:text-amber-300">{d.problem}</div>
                      ) : null}
                    </div>
                    {canChange && d.domain !== 'PEOPLE' ? (
                      <div className="flex shrink-0 gap-2">
                        <Button
                          size="sm"
                          variant={inherited ? 'default' : 'outline'}
                          disabled={busy !== null || !d.canInherit || inherited}
                          onClick={() => void choose(d.domain, 'INHERIT')}
                        >
                          {busy === d.domain && !inherited ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : null}
                          Inherit from Core
                        </Button>
                        <Button
                          size="sm"
                          variant={!inherited ? 'default' : 'outline'}
                          disabled={busy !== null || !inherited}
                          onClick={() => void choose(d.domain, 'LOCAL')}
                        >
                          {busy === d.domain && inherited ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : null}
                          Configure in Discovery
                        </Button>
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
            {!canChange ? (
              <p className="text-xs text-muted-foreground">Read only. The IT Administrator changes where each setting comes from.</p>
            ) : null}
          </>
        ) : null}
      </div>
    </GlassCard>
  );
}

export default InheritedFromCoreCard;
